import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { Toast, ToastContainer, Modal, Button } from 'react-bootstrap';
import { classifyError, displayParts, reportShownError } from '../utils/errorCodes';
import './ToastContext.scss';
import CoverArt from '../components/CoverArt';

// Minimal stroke glyphs (no emoji, no bootstrap icons) — inherit the header's
// variant color via currentColor.
const ToastIcon = ({ variant }) => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="3.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="toast-variant-icon"
        aria-hidden="true"
        focusable="false"
    >
        {variant === 'success' && <path d="M20 7L10 17l-6-5" />}
        {variant === 'danger' && <path d="M18 6L6 18M6 6l12 12" />}
        {variant === 'warning' && <><path d="M12 4.5v10" /><path d="M12 19.5v.01" /></>}
        {variant === 'info' && <><path d="M12 4.5v.01" /><path d="M12 9.5v10" /></>}
    </svg>
);

const ToastContext = createContext();

// Messages up to this length get the floor duration; every char beyond it
// buys extra reading time, capped so an error toast can't sit forever.
const MIN_TOAST_DURATION = 3000;
const MAX_TOAST_DURATION = 12000;
const READING_MS_PER_CHAR = 45;
const READING_GRACE_CHARS = 40;

const autoToastDuration = (message) => {
    const extra = Math.max(0, String(message || '').length - READING_GRACE_CHARS);
    return Math.min(MAX_TOAST_DURATION, MIN_TOAST_DURATION + extra * READING_MS_PER_CHAR);
};

// Two identical toasts stacked on top of each other are never information —
// they're a double-fired handler (a Play All click was reported arriving twice
// on Windows) or two code paths reporting the same outcome. Collapse them.
const DEDUPE_WINDOW_MS = 2000;

// Must cover the longest fade-out (card toast: .3s; bootstrap default .15s)
// so the unmount lands after the toast is fully transparent.
const TOAST_FADE_OUT_MS = 400;

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

export const ToastProvider = ({ children }) => {
    const [toasts, setToasts] = useState([]);
    const [confirmDialog, setConfirmDialog] = useState({ show: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm', confirmVariant: 'primary', secondaryText: null });

    // Monotonic — Date.now() handed two toasts raised in the same millisecond
    // the SAME React key, so one timer's removeToast dropped both.
    const nextIdRef = useRef(0);
    const recentRef = useRef(new Map()); // `${variant}|${message}` -> timestamp

    // `action` (optional): { label, onClick } renders a button in the toast —
    // e.g. an "Undo" affordance for reversible actions.
    const showToast = useCallback((message, variant = 'info', duration, action = null) => {
        // Error/warning toasts are the widest chokepoint for raw server text
        // reaching the user (~350 call sites, many passing result.error
        // verbatim). Classify here so a "Request failed (502)" or an expired-
        // token string always lands as friendly copy + (CODE), and every
        // error the user actually saw is shipped to the reporting service —
        // classified or not.
        let code = null;
        if ((variant === 'danger' || variant === 'warning') && !React.isValidElement(message)) {
            const classified = classifyError(message);
            reportShownError(classified, { surface: 'toast', variant });
            const parts = displayParts(message);
            message = parts.text;
            code = parts.code;
        }
        const now = Date.now();
        const key = `${variant}|${message}`;
        for (const [k, ts] of recentRef.current) {
            if (now - ts > DEDUPE_WINDOW_MS) recentRef.current.delete(k);
        }
        // Suppress an exact repeat, but never an actionable one — an "Undo"
        // button belongs to a specific operation and must not be swallowed.
        if (!action && recentRef.current.has(key)) return null;
        recentRef.current.set(key, now);
        const id = ++nextIdRef.current;
        const resolvedDuration = duration === undefined ? autoToastDuration(message) : duration;
        setToasts(prev => [...prev, { id, message, code, variant, duration: resolvedDuration, action }]);

        if (resolvedDuration > 0) {
            setTimeout(() => {
                removeToast(id);
            }, resolvedDuration);
        }
        return id;
    }, []);

    // Two-step dismiss: flip `closing` so react-bootstrap's Fade runs the
    // opacity-out transition, then unmount once it's done. Dropping the entry
    // straight from the array made toasts vanish in a single frame.
    const removeToast = useCallback((id) => {
        setToasts(prev => prev.map(toast => (toast.id === id && !toast.closing ? { ...toast, closing: true } : toast)));
        setTimeout(() => {
            setToasts(prev => prev.filter(toast => toast.id !== id));
        }, TOAST_FADE_OUT_MS);
    }, []);

    /**
     * Compact "entity card" toast — artwork thumb + bold title + a short
     * sentence (SoundCloud's "<track> was saved to your Library." shape).
     * No header row, no variant chrome. `link` (optional) is { label, onClick }
     * rendered inline at the end of the sentence; `action` (optional) is
     * { label, onClick } rendered as a pill button on the right (e.g. Undo).
     */
    const card = useCallback(({ artwork = null, artworkKind = 'cover', title, message, link = null, action = null, duration = 3500 }) => {
        const now = Date.now();
        const key = `card|${title}|${message}`;
        for (const [k, ts] of recentRef.current) {
            if (now - ts > DEDUPE_WINDOW_MS) recentRef.current.delete(k);
        }
        if (recentRef.current.has(key)) return null;
        recentRef.current.set(key, now);
        const id = ++nextIdRef.current;
        // A new card REPLACES any card still showing (like → like → like
        // reads as one snackbar updating, not a growing stack — and on
        // mobile the cards share one fixed slot at the bottom).
        setToasts(prev => [...prev.filter(t => t.kind !== 'card'), { id, kind: 'card', artwork, artworkKind, title, message, link, action, duration }]);
        if (duration > 0) setTimeout(() => removeToast(id), duration);
        return id;
    }, []);

    const success = useCallback((message, duration) => showToast(message, 'success', duration), [showToast]);
    const error = useCallback((message, duration) => showToast(message, 'danger', duration), [showToast]);
    const warning = useCallback((message, duration) => showToast(message, 'warning', duration), [showToast]);
    const info = useCallback((message, duration) => showToast(message, 'info', duration), [showToast]);

    /**
     * Resolves true (primary), false (Cancel / dismiss), or the string
     * 'secondary' when an optional third button is supplied via
     * `options.secondaryText`. Callers without that option keep the plain
     * boolean contract.
     */
    const confirm = useCallback((message, title = 'Confirm', options = {}) => {
        const { confirmText = 'Confirm', confirmVariant = 'primary', secondaryText = null, secondaryVariant = 'outline-secondary' } = options;
        return new Promise((resolve) => {
            const close = () => setConfirmDialog({ show: false, title: '', message: '', onConfirm: null, confirmText: 'Confirm', confirmVariant: 'primary', secondaryText: null });
            setConfirmDialog({
                show: true,
                title,
                message,
                confirmText,
                confirmVariant,
                secondaryText,
                secondaryVariant,
                onConfirm: () => { close(); resolve(true); },
                onSecondary: () => { close(); resolve('secondary'); },
                onCancel: () => { close(); resolve(false); }
            });
        });
    }, []);

    return (
        <ToastContext.Provider value={{ showToast, card, success, error, warning, info, confirm, dismiss: removeToast }}>
            {children}
            <ToastContainer position="top-end" className="toast-notification-container">
                {toasts.map(toast => toast.kind === 'card' ? (
                    <Toast
                        key={toast.id}
                        onClose={() => removeToast(toast.id)}
                        show={!toast.closing}
                        delay={toast.duration}
                        autohide={toast.duration > 0}
                        className="toast-card"
                    >
                        <Toast.Body className="toast-card-body">
                            <button
                                type="button"
                                className="toast-card-close"
                                aria-label="Dismiss"
                                onClick={() => removeToast(toast.id)}
                            >
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" aria-hidden="true">
                                    <path d="M18 6L6 18M6 6l12 12" />
                                </svg>
                            </button>
                            <div className={`toast-card-art${toast.artworkKind === 'logo' ? ' toast-card-art--logo' : ''}`}>
                                {toast.artwork
                                    ? <CoverArt src={toast.artwork} size={44} eager draggable={false} />
                                    : (
                                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <circle cx="5.5" cy="17.5" r="2.5" />
                                            <circle cx="17.5" cy="15.5" r="2.5" />
                                            <path d="M8 17.5V5l12-2v12.5" />
                                        </svg>
                                    )}
                            </div>
                            <div className="toast-card-text">
                                <div className="toast-card-title" title={toast.title}>{toast.title}</div>
                                <div className="toast-card-message">
                                    {toast.message}
                                    {toast.link && (
                                        <>
                                            {' '}
                                            <button
                                                type="button"
                                                className="toast-card-link"
                                                onClick={() => { toast.link.onClick(); removeToast(toast.id); }}
                                            >
                                                {toast.link.label}
                                            </button>
                                            .
                                        </>
                                    )}
                                </div>
                            </div>
                            {toast.action && (
                                <button
                                    type="button"
                                    className="toast-card-action"
                                    onClick={() => { toast.action.onClick(); removeToast(toast.id); }}
                                >
                                    {toast.action.label}
                                </button>
                            )}
                        </Toast.Body>
                    </Toast>
                ) : (
                    <Toast
                        key={toast.id}
                        onClose={() => removeToast(toast.id)}
                        show={!toast.closing}
                        delay={toast.duration}
                        autohide={toast.duration > 0}
                        bg={toast.variant}
                    >
                        <Toast.Header>
                            <ToastIcon variant={toast.variant} />
                            <strong className="me-auto">
                                {toast.variant === 'success' && 'Success'}
                                {toast.variant === 'danger' && 'Error'}
                                {toast.variant === 'warning' && 'Warning'}
                                {toast.variant === 'info' && 'Info'}
                            </strong>
                            {toast.code && <span className="toast-error-code">{toast.code}</span>}
                        </Toast.Header>
                        <Toast.Body className="toast-body-with-action">
                            <span>{toast.message}</span>
                            {toast.action && (
                                <Button
                                    size="sm"
                                    variant="link"
                                    className="toast-action-btn"
                                    onClick={() => { toast.action.onClick(); removeToast(toast.id); }}
                                >
                                    {toast.action.label}
                                </Button>
                            )}
                        </Toast.Body>
                    </Toast>
                ))}
            </ToastContainer>

            {/* Confirmation Modal */}
            <Modal
                show={confirmDialog.show}
                onHide={confirmDialog.onCancel}
                centered
                className="confirm-modal"
            >
                <Modal.Header closeButton className="confirm-modal-header">
                    <Modal.Title>{confirmDialog.title}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="confirm-modal-body">
                    {confirmDialog.message}
                </Modal.Body>
                <Modal.Footer className="confirm-modal-footer">
                    <Button variant="secondary" onClick={confirmDialog.onCancel}>
                        Cancel
                    </Button>
                    {confirmDialog.secondaryText && (
                        <Button variant={confirmDialog.secondaryVariant} onClick={confirmDialog.onSecondary}>
                            {confirmDialog.secondaryText}
                        </Button>
                    )}
                    <Button variant={confirmDialog.confirmVariant} onClick={confirmDialog.onConfirm}>
                        {confirmDialog.confirmText}
                    </Button>
                </Modal.Footer>
            </Modal>
        </ToastContext.Provider>
    );
};
